import * as NptActions from './npt.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { Injectable } from '@angular/core';
import { NPT_REASONS_UPLOAD_DEFAULT_PAGINATION_OPTION, TNptActionTypes } from './npt.models';
import { catchError, finalize, from, map, of, switchMap, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NptEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  getNptLinesDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNptActionTypes.GetNptLinesDetails),
      switchMap((action: NptActions.GetNptLinesDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getNptLinesDetails(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new NptActions.GetNptLinesDetailsSuccess(res)),
          catchError(() => {
            return of(new NptActions.GetNptLinesDetailsFailure());
          }),
        ),
      ),
    ),
  );

  getNptWorkstationsDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNptActionTypes.GetNptWorkstationsDetails),
      switchMap((action: NptActions.GetNptWorkstationsDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getNptWorkstationsDetails(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          switchMap((res) =>
            from([
              new NptActions.GetNptWorkstationsDetailsSuccess(res.workstationGroups),
              new NptActions.SetNptReasons(res.nptReasons),
            ]),
          ),
          catchError(() => {
            return of(new NptActions.GetNptWorkstationsDetailsFailure());
          }),
        ),
      ),
    ),
  );

  getWorkstationNpts = createEffect(() =>
    this.actions$.pipe(
      ofType(TNptActionTypes.GetWorkstationsNpts),
      switchMap((action: NptActions.GetWorkstationsNpts) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getWorkstationNpts(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          switchMap((res) =>
            from([new NptActions.GetWorkstationsNptsSuccess(res.npts), new NptActions.SetNptReasons(res.nptReasons)]),
          ),
          catchError(() => {
            return of(new NptActions.GetWorkstationsNptsFailure());
          }),
        ),
      ),
    ),
  );

  fetchNptReasonsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNptActionTypes.FetchNptReasonsList),
      switchMap((action: NptActions.FetchNptReasonsList) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.fetchNptReasonsList(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          switchMap((res) => [
            new NptActions.FetchNptReasonsListSuccess(res),
            ...(action.resetPagination
              ? [new NptActions.SetReasonsListPaginationOption(NPT_REASONS_UPLOAD_DEFAULT_PAGINATION_OPTION)]
              : []),
          ]),
          catchError(() => of(new NptActions.FetchNptReasonsListFailure())),
        ),
      ),
    ),
  );
}
